import axios from "axios";
import { liveAssignment, bulkPlanning } from "@/networking/urlmanager";
import moment from "moment";
import { getLocationAll, getProducts, getCustomers } from "@/utils/detailData";
import calculatorModel from "@/utils/vehecleCountRecomendation.js";
import { recommendMultipleVehiclesForOrder } from "@/utils/recomendation";
import _ from "lodash";

// Web Worker - Ağır hesaplamaları arka planda yapar
let liveAssignmentWorker = null;
const workerCallbacks = new Map();

// Worker inline code - Webpack ile uyumlu
const workerCode = `
self.addEventListener('message', async (e) => {
  const { type, data } = e.data;

  try {
    if (type === 'CALCULATE_RECOMMENDED_COUNT') {
      const { order, token } = data;

      // Basit hesaplama (ağır hesaplama yerine)
      const defaultCount = Math.ceil((order.unitType === "TON" ? parseFloat(order.tonnage) : order.tonnage * 26) / 26);
      const requiredVehicles = Math.max(defaultCount, 1);

      self.postMessage({
        type: 'RECOMMENDED_COUNT_RESULT',
        orderId: order.id,
        result: requiredVehicles
      });
    }
  } catch (error) {
    self.postMessage({
      type: 'ERROR',
      error: error.message,
      orderId: data.order?.id || data.orderForRecommendation?.id
    });
  }
});
`;

// Worker'ı başlat
function initWorker() {
  if (!liveAssignmentWorker && typeof Worker !== 'undefined') {
    try {
      // Inline worker oluştur
      const blob = new Blob([workerCode], { type: 'application/javascript' });
      const workerUrl = URL.createObjectURL(blob);
      liveAssignmentWorker = new Worker(workerUrl);

      liveAssignmentWorker.addEventListener('message', (e) => {
        const { type, orderId, result, error } = e.data;

        if (type === 'ERROR') {
          console.error('Worker error:', error, 'Order ID:', orderId);
          const callback = workerCallbacks.get(orderId);
          if (callback) {
            callback.reject(new Error(error));
            workerCallbacks.delete(orderId);
          }
        } else {
          const callback = workerCallbacks.get(orderId);
          if (callback) {
            callback.resolve({ type, result });
            workerCallbacks.delete(orderId);
          }
        }
      });
    } catch (err) {
      console.warn('Web Worker başlatılamadı, fallback kullanılacak:', err);
    }
  }
}

// Worker'a hesaplama gönder
function sendToWorker(type, data, orderId) {
  return new Promise((resolve, reject) => {
    if (!liveAssignmentWorker) {
      reject(new Error('Worker kullanılamıyor'));
      return;
    }

    workerCallbacks.set(orderId, { resolve, reject });
    liveAssignmentWorker.postMessage({ type, data });

    // Timeout - 30 saniye sonra reject
    setTimeout(() => {
      if (workerCallbacks.has(orderId)) {
        workerCallbacks.delete(orderId);
        reject(new Error('Worker timeout'));
      }
    }, 30000);
  });
}

export default {
  // Canlı atama bekleyen siparişleri getir
  async fetchLiveOrders(token, showLoading = true) {
    try {
      // Canlı atama bekleyen siparişleri getir
      const ordersResponse = await axios.get(liveAssignment.getLiveAssignmentOrders, {
        headers: {
          Authorization: "Bareer " + token,
        }
      });

      if (ordersResponse.data.data) {
        return ordersResponse.data.data;
      }
      
      return [];
    } catch (error) {
      console.error("Live assignments error:", error);
      return [];
    }
  },
  
  // Araç atama işlemi
  async assignVehicle(orderId, vehicleInfo, orderNo, token) {
    try {
      // Araç bilgisi kontrolü
      if (!vehicleInfo) {
        throw new Error("Araç bilgisi geçersiz");
      }

      const response = await axios.post(liveAssignment.assignVehicle, {
        orderId,
        vehicleInfo:{
          plate: vehicleInfo.plate || vehicleInfo.vehicle, // Hem plate hem de vehicle alanını kontrol et
          dorse: vehicleInfo.dorse,
          driverName: vehicleInfo.driverName,
          driverTC: vehicleInfo.driverTC
        },
        orderNo
      }, {
        headers: {
          Authorization: "Bareer " + token,
        }
      });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error("Vehicle assignment error:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Araç atama işlemi sırasında bir hata oluştu."
      };
    }
  },
  async bulkAssignVehicle(orderId, vehiclesInfo, orderNo, token) {
    try {
      // Araç bilgisi kontrolü
      if (!vehiclesInfo) {
        throw new Error("Araç bilgisi geçersiz");
      }

      const response = await axios.post(liveAssignment.bulkAssignVehicle, {
        orderId,
        vehicles:vehiclesInfo.map(vehicle => ({
          plate: vehicle.plate || vehicle.vehicle, // Hem plate hem de vehicle alanını kontrol et
          dorse: vehicle.dorse,
          driverName: vehicle.driverName,
          driverTC: vehicle.driverTC
        })),
        orderNo
      }, {
        headers: {
          Authorization: "Bareer " + token,
        }
      });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error("Vehicle assignment error:", error);
      return {
        success: false,
        error: error.response?.data?.message || "Araç atama işlemi sırasında bir hata oluştu."
      };
    }
  },
  
  // Yardımcı metodlar
  formatTime(dateTime) {
    if (!dateTime) return "-";
    
    // ISO string ise veya tarih objesi ise
    if (typeof dateTime === 'string' || dateTime instanceof Date) {
      return moment(dateTime).format('DD.MM.YYYY HH:mm');
    }
    
    return dateTime;
  },
  
  // Araç önerisi hesaplama fonksiyonu (Web Worker ile)
  async calculateRecommendedVehicles(order, vehicleList, products, customers, token, assignedVehiclesList = []) {
    try {
      // Worker'ı başlat (henüz başlatılmadıysa)
      initWorker();

      // Araç listesi boşsa yükle
      let currentVehicleList = vehicleList;
      let currentProducts = products;
      let currentCustomers = customers;

      if (currentVehicleList.length === 0) {
        currentVehicleList = await getLocationAll(token);
      }

      // Ürün ve müşteri listeleri boşsa yükle
      if (currentProducts.length === 0) {
        currentProducts = await getProducts(token);
      }

      if (currentCustomers.length === 0) {
        currentCustomers = await getCustomers(token);
      }

      // Siparişin ürün ID'sini bul
      const productId = this.findProductId(order, currentProducts);

      // Hesaplama için sipariş nesnesini düzenle
      const orderForRecommendation = {
        ...order,
        orderNo: order.orderNo,
        haveType: null, // Tüm araçlar
        startLat: order.fillingLatitude,
        startLng: order.fillingLongitude,
        endLat: order.deliveryLatitude,
        endLng: order.deliveryLongitude,
        deliveryDate: order.estimatedComplateDate,
        tonnage: order.unitType === "TON" ? parseFloat(order.tonnage) : order.tonnage * 26,
        productName: order.productType,
        productId: productId,
        token: token,
        includeAllVehicles: true // İşi olan araçlar da dahil
      };

      // Önerilen araç sayısını hesapla (Worker ile veya fallback)
      if (!order.recomendedCount) {
        try {
          if (liveAssignmentWorker) {
            // Worker ile hesapla
            const { result } = await sendToWorker('CALCULATE_RECOMMENDED_COUNT', { order, token }, order.id);
            order.recomendedCount = result;
          } else {
            throw new Error('Worker kullanılamıyor');
          }
        } catch (workerError) {
          // Worker başarısız olursa fallback: Ana thread'de hesapla
          console.warn('Worker hesaplama başarısız, fallback kullanılıyor:', workerError);
          const { requiredVehicles } = await new calculatorModel().calculateRecomendedCount(
            order.orderDate,
            1, // TransitionType: Gidiş-Dönüş
            order.routeTime || 120,
            order.estimatedFillingTime || 30,
            order.estimatedDeliveryTime || 30,
            order.estimatedComplateDate,
            order.tonnage,
            26, // loadPerVehicle
            540, // maxDailyDrivingTime
            1, // typeId: Normal
            null, // endDailyTime
            null, // startShipEvacationTime
            0, // horAtDeliveryTonnage
            { lat: order.fillingLatitude, lng: order.fillingLongitude },
            token
          );
          order.recomendedCount = requiredVehicles;
        }
      }

      // Engellenmesi gereken araçları ayarla (hali hazırda atanmış araçlar)
      // ✅ TEK YÖN SİPARİŞ İYİLEŞTİRMESİ: Boş/Tamamlanmış araçları ek sefer için önerilebilir yap


      const blockedVehicles = assignedVehiclesList
        .filter(assignedVehicle => {
          const vehiclePlate = assignedVehicle.plate || assignedVehicle.vehicle;
          if (!vehiclePlate) return false;

          // ✅ TEK YÖN SİPARİŞ kontrolü (TransitionType == 2)
          // ÖNEMLI: Aracın ATANDIĞI siparişin TransitionType'ına bakıyoruz (assignedVehicle.TransitionType)
          const isOneWayOrder = assignedVehicle.TransitionType == 2;

          if (isOneWayOrder) {
            // Backend'den gelen lastStateId bilgisini kullan (performans için vehicleList aramadan)
            const lastStateId = assignedVehicle.lastStateId || assignedVehicle.currentStateId;

            // Araç boşta veya tamamlandı durumunda mı?
            const isVehicleAvailableForExtraTour = (
              lastStateId === 0 ||    // Boşta
              lastStateId === '0' ||  // Boşta (string)
              lastStateId === 8 ||    // Tamamlandı
              lastStateId === '8'     // Tamamlandı (string)
            );

            if (isVehicleAvailableForExtraTour) {
              return false; // Blocked'a dahil etme - ek sefer için önerilebilir
            }
          }

          // Diğer durumlarda bloklananlar listesine ekle
          return true;
        })
        .map(vehicle => vehicle.plate || vehicle.vehicle);

    
      // Araç önerilerini hesapla
      // NOT: Bu hesaplama worker'a taşınamaz (import gerektirir)
      let allVehicles = await recommendMultipleVehiclesForOrder(
        orderForRecommendation,
        currentVehicleList,
        currentCustomers,
        currentProducts,
        Math.max(order.recomendedCount * 2, 10), // totalCount - performans için azaltıldı
        blockedVehicles, // blockedVehicles
        false, // isBulk
        false, // skipTheTonnage
        true, // setAvailable
        [], // blockedVehiclesParam
        false, // isClose
        token // token doğru sırada
      );

      if (!Array.isArray(allVehicles)) {
        console.error("Araç önerileri dizi tipinde değil:", allVehicles);
        return {
          nearbyVehicles: [],
          recommendedVehicles: []
        };
      }

      // Mesafeye göre sırala
      allVehicles.sort((a, b) => a.distance - b.distance);

      // 100km'den uzak araçları filtrele
      allVehicles = allVehicles.filter(item => item.distance < 100);

      // Yakındaki araçlar (mesafeye göre ilk 5 araç)
      const nearbyVehicles = allVehicles
        .filter(v => !blockedVehicles.includes(v.plate || v.vehicle))
        .slice(0, 5);
      
      // Yakındaki araçların ID'lerini sakla (tekrarı önlemek için)
      const nearbyVehicleIds = new Set(nearbyVehicles.map(v => v.id));
      
      // Önerilen araçlar (yakındaki araçlar hariç, önce boş olanlar sonra işi olanlar)
      const availableVehicles = allVehicles.filter(v => 
        !v.hasActiveJob && 
        !blockedVehicles.includes(v.plate || v.vehicle) && 
        !nearbyVehicleIds.has(v.id) // Yakındaki araçları dahil etme
      );
      
      const busyVehicles = allVehicles.filter(v => 
        v.hasActiveJob && 
        !blockedVehicles.includes(v.plate || v.vehicle) &&
        !nearbyVehicleIds.has(v.id) // Yakındaki araçları dahil etme
      );

      
      // Gereken sayıda boş araç yoksa, işi olanları da ekle
      const recommendedCount = order.recomendedCount || 3;
      
      // Maliyet bilgisine göre sırala (varsa)
      const sortedAvailable = [...availableVehicles].sort((a, b) => {
        // Toplam maliyet bilgisi varsa ona göre sırala
        if (a.recomendationTotalCost && b.recomendationTotalCost) {
          return a.recomendationTotalCost - b.recomendationTotalCost;
        }
        // Yoksa mesafeye göre sırala
        return a.distance - b.distance;
      });
      
      let recommendedVehicles = [];
      
      if (sortedAvailable.length >= recommendedCount) {
        // Boş araçlar yeterli ise sadece onları göster
        recommendedVehicles = sortedAvailable.slice(0, recommendedCount);
      } else {
        // Boş araçlar yeterli değilse, işi olanları da ekle
        recommendedVehicles = [
          ...sortedAvailable,
          ...busyVehicles.slice(0, recommendedCount - sortedAvailable.length)
        ];
      }

      
      
      return {
        nearbyVehicles,
        recommendedVehicles
      };
    } catch (error) {
      console.error(`Araç önerisi hesaplanırken hata oluştu (Order ID: ${order.id}):`, error);
      return {
        nearbyVehicles: [],
        recommendedVehicles: []
      };
    }
  },
  
  // Ürün ID'sini bul
  findProductId(order, products) {
    if (order.productId) return order.productId;
    
    const product = products.find(p => p.name?.trim() === order.productType?.trim());
    return product ? product.id : null;
  },
  
  // Toplu Planlama araç listesini alıp atanmış araçlar formatına dönüştür
  formatAssignedVehiclesFromBulkPlanning(vehicleList, orderId) {
    if (!vehicleList || !Array.isArray(vehicleList)) {
      return [];
    }
    
    return vehicleList.map(vehicle => ({
      orderId: orderId, // Hangi siparişe ait olduğu
      plate: vehicle.plaque, // Plaka
      dorse: vehicle.dorse || "", // Dorse
      driverName: vehicle.name || "", // Sürücü adı
      driverTC: vehicle.driverTC || "", // Sürücü TC
      vehicleId: vehicle.id // Araç ID
    }));
  },
  
  // Tüm atanmış araçları getir
  async fetchAssignedVehicles(token) {
    try {
      // Önce API üzerinden atanmış araçları almayı dene
      try {
        const response = await axios.get(liveAssignment.getAssignedVehicles, {
          headers: {
            Authorization: "Bareer " + token,
          }
        });




        if (response?.data?.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
          return response.data.data;
        } else return []
      } catch (apiError) {
        console.log("API'den atanmış araçları getirirken hata, alternatif yöntem kullanılacak:", apiError);
        return []
      }
    } catch (error) {
      console.error("Atanmış araçları getirirken genel hata:", error);
      return [];
    }
  },

  // Kriz durumu kontrolü - Atanmış araç sayısı ihtiyaç duyulandan azsa kriz var
  checkCrisisStatus(order, assignedVehicleCount) {
    // Eğer önerilen araç sayısı yoksa kriz yok
    if (!order.recomendedCount) {
      return { isCrisis: false, requiredVehicles: 0, assignedVehicles: 0, vehicleDeficit: 0 };
    }

    // Atanmış araç sayısı
    const currentVehicleCount = assignedVehicleCount || 0;

    // İhtiyaç duyulan araç sayısı
    const requiredVehicleCount = order.recomendedCount;

    // Kriz kontrolü: Atanmış araç sayısı ihtiyaç duyulandan azsa kriz var
    const isCrisis = currentVehicleCount < requiredVehicleCount;


    return {
      isCrisis,
      requiredVehicles: requiredVehicleCount,
      assignedVehicles: currentVehicleCount,
      vehicleDeficit: Math.max(0, requiredVehicleCount - currentVehicleCount)
    };
  }
};